import { useCallback, useEffect, useRef, useState } from "react";

import {
  cancelImportSession,
  createImportSession,
  finalizeImportSession,
  getActiveImportSession,
  getImportSession,
  safeErrorMessage,
  uploadImportFile,
  type DatasetLocation,
} from "./api";
import type { ImportSession, ImportSessionLimits } from "./types";


export type QueuedImportFile = {
  file: File;
  index: number;
  status: "pending" | "uploading" | "ready" | "error" | "cancelled";
  error: string;
};

export function useImportQueue(
  location: DatasetLocation,
  onChanged: (completed?: ImportSession) => Promise<void>,
) {
  const [session, setSession] = useState<ImportSession | null>(null);
  const [entries, setEntries] = useState<QueuedImportFile[]>([]);
  const [limits, setLimits] = useState<ImportSessionLimits | null>(null);
  const [paused, setPaused] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [reconnecting, setReconnecting] = useState(false);
  const entriesRef = useRef<QueuedImportFile[]>([]);
  const sessionRef = useRef<ImportSession | null>(null);
  const limitsRef = useRef<ImportSessionLimits | null>(null);
  const pausedRef = useRef(false);
  const cancelledRef = useRef(false);
  const runningRef = useRef(false);
  const controllers = useRef(new Map<number, AbortController>());
  const drainRef = useRef<() => Promise<void>>(async () => undefined);
  const previousAnalysisState = useRef("");

  const replaceEntries = useCallback((next: QueuedImportFile[]) => {
    entriesRef.current = next;
    setEntries([...next]);
  }, []);

  const replaceSession = useCallback((next: ImportSession | null) => {
    const current = sessionRef.current;
    if (next && current?.id === next.id && next.readyFiles < current.readyFiles) return;
    sessionRef.current = next;
    setSession(next);
  }, []);

  useEffect(() => {
    let active = true;
    void getActiveImportSession(location).then((found) => {
      if (!active) return;
      replaceSession(found);
    }).catch((cause) => {
      if (!active) return;
      setError(safeErrorMessage(cause));
    });
    return () => { active = false; };
  }, [location.clientId, location.datasetId, replaceSession]);

  useEffect(() => {
    if (!session?.analysis) return;
    if (!["queued", "running", "retry_wait", "cancel_requested"].includes(session.analysis.state)) return;
    let disposed = false;
    let timer = 0;
    let failures = 0;
    const schedule = (delay: number) => {
      timer = window.setTimeout(() => { void poll(); }, delay);
    };
    const poll = async () => {
      if (disposed) return;
      if (document.visibilityState === "hidden") {
        schedule(15_000);
        return;
      }
      try {
        const updated = await getImportSession(location, session.id);
        if (disposed) return;
        failures = 0;
        setReconnecting(false);
        replaceSession(updated);
      } catch {
        if (disposed) return;
        failures += 1;
        setReconnecting(true);
      }
      if (!disposed) schedule(importPollingDelay(failures));
    };
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      window.clearTimeout(timer);
      void poll();
    };
    document.addEventListener("visibilitychange", handleVisibility);
    schedule(2_000);
    return () => {
      disposed = true;
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [location, replaceSession, session?.analysis?.state, session?.id]);

  useEffect(() => {
    const state = session?.analysis?.state ?? "";
    const previous = previousAnalysisState.current;
    previousAnalysisState.current = state;
    if (!["queued", "running", "retry_wait", "cancel_requested"].includes(previous)) return;
    if (!["succeeded", "failed", "cancelled"].includes(state)) return;
    void onChanged(session ?? undefined);
  }, [onChanged, session]);

  const uploadOne = useCallback(async (entry: QueuedImportFile) => {
    const currentSession = sessionRef.current;
    const currentLimits = limitsRef.current;
    if (!currentSession || !currentLimits) return;
    let attempt = 0;
    while (attempt <= currentLimits.maxRetries) {
      if (pausedRef.current || cancelledRef.current) return;
      const controller = new AbortController();
      controllers.current.set(entry.index, controller);
      updateEntry(entriesRef.current, entry.index, "uploading", "");
      setEntries([...entriesRef.current]);
      try {
        const updated = await uploadImportFile(
          location,
          currentSession.id,
          entry.file,
          entry.index,
          controller.signal,
        );
        replaceSession(updated);
        updateEntry(entriesRef.current, entry.index, "ready", "");
        setEntries([...entriesRef.current]);
        return;
      } catch (cause) {
        if (controller.signal.aborted || pausedRef.current || cancelledRef.current) {
          const status = cancelledRef.current ? "cancelled" : "pending";
          updateEntry(entriesRef.current, entry.index, status, "");
          setEntries([...entriesRef.current]);
          return;
        }
        attempt += 1;
        if (attempt > currentLimits.maxRetries) {
          updateEntry(entriesRef.current, entry.index, "error", safeErrorMessage(cause));
          setEntries([...entriesRef.current]);
          return;
        }
        await retryDelay(attempt);
      } finally {
        controllers.current.delete(entry.index);
      }
    }
  }, [location, replaceSession]);

  const worker = useCallback(async () => {
    while (!pausedRef.current && !cancelledRef.current) {
      const entry = entriesRef.current.find((candidate) => candidate.status === "pending");
      if (!entry) return;
      entry.status = "uploading";
      setEntries([...entriesRef.current]);
      await uploadOne(entry);
    }
  }, [uploadOne]);

  const drain = useCallback(async () => {
    const currentSession = sessionRef.current;
    const currentLimits = limitsRef.current;
    if (!currentSession || !currentLimits || runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    setError("");
    const workerCount = Math.min(currentLimits.recommendedConcurrency, currentLimits.maxConcurrency);
    try {
      await Promise.all(Array.from({ length: workerCount }, () => worker()));
      if (pausedRef.current || cancelledRef.current) return;
      if (entriesRef.current.some((entry) => entry.status === "error")) return;
      if (entriesRef.current.some((entry) => entry.status !== "ready")) return;
      const completed = await finalizeImportSession(location, currentSession.id);
      replaceSession(completed);
      replaceEntries([]);
      await onChanged(completed);
    } catch (cause) {
      setError(safeErrorMessage(cause));
    } finally {
      runningRef.current = false;
      setRunning(false);
      const hasPendingFiles = entriesRef.current.some((entry) => entry.status === "pending");
      if (!pausedRef.current && !cancelledRef.current && hasPendingFiles) {
        window.setTimeout(() => { void drainRef.current(); }, 0);
      }
    }
  }, [location, onChanged, replaceEntries, replaceSession, worker]);
  drainRef.current = drain;

  const start = useCallback(async (
    files: File[],
    role: string,
    mode: "files" | "folder" | "zip",
    expectedRevision: number,
  ) => {
    cancelledRef.current = false;
    pausedRef.current = false;
    setPaused(false);
    setError("");
    const created = await createImportSession(location, {
      expectedRevision,
      role,
      mode,
      expectedFiles: files.length,
      expectedBytes: files.reduce((total, file) => total + file.size, 0),
      idempotencyKey: crypto.randomUUID(),
    });
    limitsRef.current = created.limits;
    setLimits(created.limits);
    replaceSession(created.session);
    replaceEntries(files.map((file, index) => ({ file, index, status: "pending", error: "" })));
    window.setTimeout(() => { void drainRef.current(); }, 0);
  }, [location, replaceEntries, replaceSession]);

  const resumeWithFiles = useCallback((files: File[]) => {
    const current = sessionRef.current;
    if (!current) return;
    const matched = files.map((file, index): QueuedImportFile => {
      const relativePath = file.webkitRelativePath || file.name;
      const stored = current.files.find((candidate) => {
        return candidate.index === index
          && candidate.relativePath === relativePath
          && candidate.expectedSize === `${file.size}`;
      });
      const status = stored?.status === "ready" ? "ready" : "pending";
      return { file, index, status, error: "" };
    });
    replaceEntries(matched);
    cancelledRef.current = false;
    pausedRef.current = false;
    setPaused(false);
    if (!limitsRef.current) {
      const fallback: ImportSessionLimits = {
        maxFiles: current.expectedFiles,
        maxBytesPerFile: Number.MAX_SAFE_INTEGER,
        maxTotalBytes: Number.MAX_SAFE_INTEGER,
        recommendedConcurrency: 4,
        maxConcurrency: 8,
        maxRetries: 2,
      };
      limitsRef.current = fallback;
      setLimits(fallback);
    }
    window.setTimeout(() => { void drainRef.current(); }, 0);
  }, [replaceEntries]);

  const pause = useCallback(() => {
    pausedRef.current = true;
    setPaused(true);
    for (const controller of controllers.current.values()) controller.abort();
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    setPaused(false);
    window.setTimeout(() => { void drainRef.current(); }, 0);
  }, []);

  const retry = useCallback(() => {
    for (const entry of entriesRef.current) {
      if (entry.status === "error") entry.status = "pending";
    }
    setEntries([...entriesRef.current]);
    resume();
  }, [resume]);

  const cancel = useCallback(async () => {
    const current = sessionRef.current;
    if (!current) return;
    cancelledRef.current = true;
    for (const controller of controllers.current.values()) controller.abort();
    for (const entry of entriesRef.current) entry.status = "cancelled";
    setEntries([...entriesRef.current]);
    try {
      const cancelled = await cancelImportSession(location, current.id);
      replaceSession(cancelled);
      replaceEntries([]);
    } catch (cause) {
      setError(safeErrorMessage(cause));
    }
  }, [location, replaceEntries, replaceSession]);

  return {
    session,
    entries,
    limits,
    paused,
    running,
    error,
    reconnecting,
    start,
    resumeWithFiles,
    pause,
    resume,
    retry,
    cancel,
  };
}

function updateEntry(
  entries: QueuedImportFile[],
  index: number,
  status: QueuedImportFile["status"],
  error: string,
) {
  const entry = entries.find((candidate) => candidate.index === index);
  if (!entry) return;
  entry.status = status;
  entry.error = error;
}

async function retryDelay(attempt: number) {
  const milliseconds = Math.min(250 * (2 ** (attempt - 1)), 2000);
  await new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function importPollingDelay(failures: number) {
  if (failures <= 0) return 2_000;
  return Math.min(2_000 * (2 ** failures), 15_000);
}
